import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { QrCode, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import ChannelBehaviorEditor from '@/components/chatbot/ChannelBehaviorEditor';

export const InstagramChannel = ({
  chatbotId,
  channels,
  loadChannels,
  disconnectChannel,
  openQR,
}) => {
  const connected = !!channels.instagram?.connected;
  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-pink-50/30">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">Instagram Business</h4>
            <p className="text-sm text-slate-500">Auto-reply to DMs</p>
          </div>
        </div>
        {connected && (
          <Badge className="bg-pink-100 text-pink-700">
            <CheckCircle className="w-3 h-3 mr-1" /> Connected
          </Badge>
        )}
      </div>

      {!connected ? (
        <div className="space-y-3">
          <div className="rounded-xl border-2 border-dashed border-pink-200 bg-pink-50/40 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                <QrCode className="w-5 h-5 text-pink-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-pink-900">Easiest: connect from your phone</p>
                <p className="text-xs text-pink-700 mt-0.5">Scan a QR code, approve in Instagram — done.</p>
              </div>
              <Button
                size="sm"
                onClick={() => openQR('instagram', 'Instagram')}
                className="bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] hover:opacity-90 text-white"
                data-testid="instagram-qr-connect-btn"
              >
                <QrCode className="w-4 h-4 mr-1.5" /> Show QR
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span>or connect from this browser</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>
          <p className="text-sm text-slate-600">Sign in with your Facebook account that has an Instagram Business profile linked.</p>
          <Button
            onClick={async () => {
              try {
                const response = await api.get(`/chatbots/${chatbotId}/channels/instagram/oauth`);
                window.open(response.data.oauth_url, 'instagram-auth', 'width=600,height=700,popup=1');

                const handleMessage = (event) => {
                  if (event.data.type === 'INSTAGRAM_AUTH_SUCCESS') {
                    toast.success(`Connected to ${event.data.username}`);
                    loadChannels();
                    window.removeEventListener('message', handleMessage);
                  } else if (event.data.type === 'INSTAGRAM_AUTH_ERROR') {
                    toast.error(event.data.error || 'Instagram connection failed');
                    window.removeEventListener('message', handleMessage);
                  }
                };
                window.addEventListener('message', handleMessage);
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to start Instagram connection');
              }
            }}
            className="w-full bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] hover:opacity-90 text-white"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            Sign in with Instagram
          </Button>
          <p className="text-xs text-slate-500 text-center">Requires an Instagram Business profile linked to a Facebook Page</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
            <div className="flex-1">
              <p className="text-sm font-medium text-pink-900">Connected to @{channels.instagram?.username || 'Instagram'}</p>
              <p className="text-xs text-pink-700">
                {channels.instagram?.webhook_subscribed === false
                  ? <span className="text-amber-700">⚠ Webhook subscription missing on the linked Facebook page — DMs won&apos;t arrive. Click &quot;Fix webhook&quot; below.</span>
                  : 'Auto-replying to DMs'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => disconnectChannel('instagram')} className="text-red-600 border-red-200 hover:bg-red-50">
              Disconnect
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const r = await api.post(`/chatbots/${chatbotId}/channels/instagram/resubscribe-webhook`);
                  if (r.data?.ok) toast.success('IG page re-subscribed — DMs will now arrive');
                  else toast.error('Re-subscribe failed');
                  loadChannels?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Re-subscribe failed');
                }
              }}
              data-testid="ig-resubscribe-btn"
            >
              Fix webhook (re-subscribe IG page)
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  const r = await api.get(`/chatbots/${chatbotId}/channels/instagram/webhook-status`);
                  const d = r.data;
                  toast.message('Instagram diagnostics', {
                    description: `Subscribed: ${d.webhook_subscribed ? 'yes' : 'NO'} • Last DM webhook: ${d.last_webhook_received_at || 'never'}${d.webhook_subscribe_error ? ` • Error: ${d.webhook_subscribe_error}` : ''}`,
                    duration: 12000,
                  });
                } catch (e) { toast.error('Could not load diagnostics'); }
              }}
              data-testid="ig-diagnostics-btn"
            >
              Diagnostics
            </Button>
          </div>
          <ChannelBehaviorEditor chatbotId={chatbotId} platform="instagram" label="Instagram" />
        </div>
      )}
    </div>
  );
};

export default InstagramChannel;
